/** Add or edit a customer, including the admin-defined extra fields (§4.3). */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Save } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { Alert, Button, Card, Field, Skeleton, inputClass } from '../../components/ui';

interface ParameterValue {
  id: string;
  code: string;
  label: string;
  meta: Record<string, unknown> | null;
}

interface ParameterGroup {
  key: string;
  label: string;
  values: ParameterValue[];
}

interface CustomFieldDef {
  id: string;
  key: string;
  label: string;
  type: 'TEXT' | 'NUMBER' | 'DATE' | 'DROPDOWN';
  required: boolean;
  parameterGroup: { values: ParameterValue[] } | null;
}

interface CustomerDetail {
  id: string;
  name: string;
  tin: string | null;
  addressLine: string | null;
  barangay: string | null;
  city: string | null;
  province: string | null;
  zip: string | null;
  categoryId: string | null;
  industryId: string | null;
  isGovernment: boolean;
  notes: string | null;
  customFields: Record<string, unknown> | null;
}

const BLANK = {
  name: '',
  tin: '',
  addressLine: '',
  barangay: '',
  city: '',
  province: '',
  zip: '',
  categoryId: '',
  industryId: '',
  isGovernment: false,
  notes: '',
};

export function CustomerFormPage(): JSX.Element {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(BLANK);
  const [custom, setCustom] = useState<Record<string, string>>({});
  const [governmentTouched, setGovernmentTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parameters = useQuery({
    queryKey: ['parameters'],
    queryFn: () => api<{ data: ParameterGroup[] }>('/parameters'),
  });
  const fields = useQuery({
    queryKey: ['custom-fields'],
    queryFn: () => api<{ data: CustomFieldDef[] }>('/parameters/custom-fields'),
  });
  const existing = useQuery({
    queryKey: ['customer', id],
    queryFn: () => api<{ data: CustomerDetail }>(`/customers/${id}`),
    enabled: editing,
  });

  useEffect(() => {
    const row = existing.data?.data;
    if (!row) return;
    setForm({
      name: row.name,
      tin: row.tin ?? '',
      addressLine: row.addressLine ?? '',
      barangay: row.barangay ?? '',
      city: row.city ?? '',
      province: row.province ?? '',
      zip: row.zip ?? '',
      categoryId: row.categoryId ?? '',
      industryId: row.industryId ?? '',
      isGovernment: row.isGovernment,
      notes: row.notes ?? '',
    });
    const values: Record<string, string> = {};
    for (const [key, value] of Object.entries(row.customFields ?? {})) {
      values[key] = String(value ?? '');
    }
    setCustom(values);
  }, [existing.data]);

  const categories = parameters.data?.data.find((g) => g.key === 'customer_category')?.values ?? [];
  const industries = parameters.data?.data.find((g) => g.key === 'industry')?.values ?? [];

  /** LGU, national agency and GOCC imply a government client until overridden. */
  const onCategoryChange = (categoryId: string): void => {
    const category = categories.find((value) => value.id === categoryId);
    const government = (category?.meta as { government?: boolean } | null)?.government === true;
    setForm((current) => ({
      ...current,
      categoryId,
      isGovernment: governmentTouched ? current.isGovernment : government,
    }));
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        ...form,
        tin: form.tin || undefined,
        categoryId: form.categoryId || undefined,
        industryId: form.industryId || undefined,
        customFields: custom,
      };
      return editing
        ? api<{ data: { id: string } }>(`/customers/${id}`, { method: 'PATCH', body })
        : api<{ data: { id: string } }>('/customers', { method: 'POST', body });
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      void queryClient.invalidateQueries({ queryKey: ['customer', id] });
      navigate(`/customers/${result.data.id}`);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not save the customer.'),
  });

  if (editing && existing.isLoading) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <header>
        <h1 className="text-xl font-bold text-navy dark:text-white">
          {editing ? 'Edit customer' : 'New customer'}
        </h1>
        <p className="text-sm text-slate-500">
          The company name must be unique. Everything else can be filled in later.
        </p>
      </header>

      {error && <Alert tone="critical">{error}</Alert>}

      <Card>
        <form
          className="grid gap-4 sm:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault();
            setError(null);
            save.mutate();
          }}
        >
          <div className="sm:col-span-2">
            <Field label="Company name" required>
              <input
                className={inputClass}
                required
                minLength={2}
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
            </Field>
          </div>

          <Field label="TIN" hint="Format 000-000-000-00000.">
            <input
              className={inputClass}
              placeholder="000-000-000-00000"
              value={form.tin}
              onChange={(event) => setForm({ ...form, tin: event.target.value })}
            />
          </Field>

          <Field label="Category">
            <select
              className={inputClass}
              value={form.categoryId}
              onChange={(event) => onCategoryChange(event.target.value)}
            >
              <option value="">Not set</option>
              {categories.map((value) => (
                <option key={value.id} value={value.id}>
                  {value.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Industry">
            <select
              className={inputClass}
              value={form.industryId}
              onChange={(event) => setForm({ ...form, industryId: event.target.value })}
            >
              <option value="">Not set</option>
              {industries.map((value) => (
                <option key={value.id} value={value.id}>
                  {value.label}
                </option>
              ))}
            </select>
          </Field>

          <div className="flex items-end">
            <label className="flex items-center gap-2 pb-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-navy focus:ring-brand"
                checked={form.isGovernment}
                onChange={(event) => {
                  setGovernmentTouched(true);
                  setForm({ ...form, isGovernment: event.target.checked });
                }}
              />
              Government client
              <span className="text-xs text-slate-500">(5% withholding VAT)</span>
            </label>
          </div>

          <div className="sm:col-span-2">
            <Field label="Address">
              <input
                className={inputClass}
                value={form.addressLine}
                onChange={(event) => setForm({ ...form, addressLine: event.target.value })}
              />
            </Field>
          </div>
          <Field label="Barangay">
            <input
              className={inputClass}
              value={form.barangay}
              onChange={(event) => setForm({ ...form, barangay: event.target.value })}
            />
          </Field>
          <Field label="City / municipality">
            <input
              className={inputClass}
              value={form.city}
              onChange={(event) => setForm({ ...form, city: event.target.value })}
            />
          </Field>
          <Field label="Province">
            <input
              className={inputClass}
              value={form.province}
              onChange={(event) => setForm({ ...form, province: event.target.value })}
            />
          </Field>
          <Field label="ZIP">
            <input
              className={inputClass}
              value={form.zip}
              onChange={(event) => setForm({ ...form, zip: event.target.value })}
            />
          </Field>

          {(fields.data?.data.length ?? 0) > 0 && (
            <div className="sm:col-span-2">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Additional details
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                {fields.data?.data.map((field) => (
                  <Field key={field.id} label={field.label} required={field.required}>
                    {field.type === 'DROPDOWN' ? (
                      <select
                        className={inputClass}
                        required={field.required}
                        value={custom[field.key] ?? ''}
                        onChange={(event) =>
                          setCustom({ ...custom, [field.key]: event.target.value })
                        }
                      >
                        <option value="">Not set</option>
                        {field.parameterGroup?.values.map((value) => (
                          <option key={value.id} value={value.id}>
                            {value.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className={inputClass}
                        required={field.required}
                        type={
                          field.type === 'NUMBER' ? 'number' : field.type === 'DATE' ? 'date' : 'text'
                        }
                        value={custom[field.key] ?? ''}
                        onChange={(event) =>
                          setCustom({ ...custom, [field.key]: event.target.value })
                        }
                      />
                    )}
                  </Field>
                ))}
              </div>
            </div>
          )}

          <div className="sm:col-span-2">
            <Field label="Notes">
              <textarea
                className={`${inputClass} h-24`}
                value={form.notes}
                onChange={(event) => setForm({ ...form, notes: event.target.value })}
              />
            </Field>
          </div>

          <div className="flex gap-2 sm:col-span-2">
            <Button type="submit" busy={save.isPending}>
              <Save aria-hidden="true" className="h-4 w-4" />
              {editing ? 'Save changes' : 'Create customer'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => navigate(-1)}>
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
